import { User } from 'firebase/auth'
import JSZip from 'jszip'
import { LockedSalaryData } from '@/types/lockedSalaryData'
import { SalarySpecificationService } from './salarySpecificationService'
import { getAmountToPay } from './lockedDataAdapter'
import { userProfileService } from './userProfileService'

interface CompanyInfo {
  name?: string
  orgNumber?: string
  address?: string
  city?: string
  postalCode?: string
}

/**
 * Build the minimal spec data structure required for PDF generation
 * from locked salary data
 */
function buildSpecDataFromLocked(
  data: LockedSalaryData,
  companyInfo?: CompanyInfo
) {
  return {
    driver: {
      id: data.driverId,
      primaryName: data.driverInfo.name,
      personalCode: data.driverInfo.personalCode,
      employeeNumber: data.driverInfo.employeeNumber,
      bankAccount: data.driverInfo.bankAccount,
      email: data.driverInfo.email
    },
    period: data.period,
    earnings: [],
    compensations: [],
    salaryData: {
      driverId: data.driverId,
      driverName: data.driverInfo.name,
      grossEarnings: data.aggregatedTotals.grossEarnings,
      netEarnings: data.aggregatedTotals.netEarnings,
      tips: data.aggregatedTotals.tips,
      cashReceived: data.aggregatedTotals.cashReceived,
      vatAmount: data.aggregatedTotals.vatAmount,
      shareableAmount: data.aggregatedTotals.shareableAmount,
      afterFleetCommission: data.aggregatedTotals.afterFleetCommission,
      vacationPay: data.aggregatedTotals.vacationPay,
      grossSalary: data.aggregatedTotals.grossSalary,
      finalGrossSalary: data.totalGrossSalary,
      netSalary: data.aggregatedTotals.netSalary,
      finalNetSalary: data.totalNetAfterTax,
      taxAmount: data.aggregatedTotals.taxAmount,
      effectiveTaxRate: data.aggregatedTotals.effectiveTaxRate,
      untaxedTips: data.aggregatedTotals.untaxedTips,
      finalNetPayout: data.aggregatedTotals.finalNetPayout,
      amountToPay: getAmountToPay(data.aggregatedTotals),
      netSalaryCompensations: data.aggregatedTotals.netSalaryCompensations,
      netSalaryDeductions: data.aggregatedTotals.netSalaryDeductions
    },
    settings: {
      driverRevenueShare: data.fleetSettingsUsed.driverRevenueShare * 100,
      vacationPayRate: data.fleetSettingsUsed.vacationPayRate * 100,
      taxRate: data.fleetSettingsUsed.taxRate * 100,
      vatRate: data.fleetSettingsUsed.vatRate * 100,
      feeOnTips: data.fleetSettingsUsed.feeOnTips
    },
    companyInfo
  }
}

/**
 * Load company info for the current user
 */
async function loadCompanyInfo(userId: string): Promise<CompanyInfo | undefined> {
  try {
    const userProfile = await userProfileService.getUserProfile(userId)
    if (userProfile) {
      return {
        name: userProfile.companyName,
        orgNumber: userProfile.organizationNumber,
        address: userProfile.address,
        city: userProfile.city,
        postalCode: userProfile.postalCode
      }
    }
  } catch {
    // Continue without company info
  }
  return undefined
}

/**
 * Generate and download a single PDF for a locked salary data entry
 */
export async function generateAndDownloadPdf(
  user: User,
  data: LockedSalaryData,
  period: string,
  includeCompanyInFilename?: boolean
): Promise<void> {
  const companyInfo = await loadCompanyInfo(user.uid)
  const specData = buildSpecDataFromLocked(data, companyInfo)
  const formattedPeriod = SalarySpecificationService.formatPeriodForDisplay(period)
  const blob = await SalarySpecificationService.generatePdfBlob(user, specData as any, formattedPeriod)

  // Download the PDF
  const filename = SalarySpecificationService.generateFilename(
    data.driverInfo.name,
    period,
    includeCompanyInFilename ? data.companyInfo?.name : undefined
  )
  downloadBlob(blob, filename)
}

/**
 * Result of bulk PDF generation
 */
export interface BulkPdfResult {
  successCount: number
  errors: string[]
}

/**
 * Generate PDFs for all locked data entries and download as ZIP
 */
export async function generateBulkPdfsAsZip(
  user: User,
  lockedDataList: LockedSalaryData[],
  period: string,
  onProgress?: (current: number, total: number, errors: string[]) => void,
  includeCompanyInFilename?: boolean
): Promise<BulkPdfResult> {
  const zip = new JSZip()
  const errors: string[] = []

  // Load company info once
  const companyInfo = await loadCompanyInfo(user.uid)

  for (let i = 0; i < lockedDataList.length; i++) {
    const data = lockedDataList[i]

    try {
      const specData = buildSpecDataFromLocked(data, companyInfo)
      const formattedPeriod = SalarySpecificationService.formatPeriodForDisplay(period)
      const blob = await SalarySpecificationService.generatePdfBlob(user, specData as any, formattedPeriod)

      const filename = SalarySpecificationService.generateFilename(
        data.driverInfo.name,
        period,
        includeCompanyInFilename ? data.companyInfo?.name : undefined
      )
      zip.file(filename, blob)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Okänt fel'
      errors.push(`${data.driverInfo.name}: ${message}`)
    }

    onProgress?.(i + 1, lockedDataList.length, errors)
  }

  const successCount = lockedDataList.length - errors.length

  if (successCount > 0) {
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const formattedPeriod = period.replace(/-/g, '')
    const zipFilename = `lonespecifikationer_${formattedPeriod}.zip`
    downloadBlob(zipBlob, zipFilename)
  }

  return { successCount, errors }
}

/**
 * Helper to download a blob as a file
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
